import React from "react";
import { render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { SearchResults } from "./SearchResults";

/**
 * setupTests stubs matchMedia to answer false for every query, which would pin
 * these to the narrow layout. Answer real width comparisons instead so both
 * branches of useWideLayout can be exercised.
 */
const setViewportWidth = (width: number) => {
  (window as any).matchMedia = (query: string) => {
    const min = /min-width:\s*(\d+(?:\.\d+)?)px/.exec(query);
    const max = /max-width:\s*(\d+(?:\.\d+)?)px/.exec(query);
    let matches = true;
    if (min) matches = matches && width >= parseFloat(min[1]);
    if (max) matches = matches && width <= parseFloat(max[1]);
    if (!min && !max) matches = false;
    return {
      matches,
      media: query,
      onchange: null,
      addListener() {},
      removeListener() {},
      addEventListener() {},
      removeEventListener() {},
      dispatchEvent: () => true,
    };
  };
};

const company = (i: number) => ({
  _id: `ws/company/c-${i}`,
  value: {
    name: { value: `Company ${i}` },
    headquarters: { value: { city: { value: "Boston" } } },
    number_of_employees: { value: 42 },
  },
});

const renderResults = () =>
  render(
    <SearchResults
      loading={false}
      results={[company(1), company(2)]}
      onCompanyClick={() => {}}
    />,
  );

// Each company name appearing EXACTLY ONCE is the real guard against both
// layouts mounting. getAllByText ignores CSS visibility, so the old code --
// which rendered the table and the cards and hid one with display:none --
// would find every name twice and fail here.

describe("SearchResults layout", () => {
  it("renders the table, and only the table, on a wide viewport", () => {
    setViewportWidth(1440);
    renderResults();

    expect(screen.getByRole("table")).toBeInTheDocument();
    // header row + one row per company
    expect(screen.getAllByRole("row")).toHaveLength(3);
    expect(screen.getAllByText("Company 1")).toHaveLength(1);
    expect(screen.getAllByText("Company 2")).toHaveLength(1);
  });

  it("renders the cards, and no table, on a narrow viewport", () => {
    setViewportWidth(600);
    renderResults();

    expect(screen.queryByRole("table")).not.toBeInTheDocument();
    expect(screen.getAllByText("Company 1")).toHaveLength(1);
    expect(screen.getAllByText("Company 2")).toHaveLength(1);
  });
});

describe("SearchResults cards", () => {
  it("keeps each card's DOM across a re-render", () => {
    // A re-render that remounted the cards would replace the source icon under
    // the pointer, and react-tooltip would move its open tooltip to the page's
    // first icon.
    setViewportWidth(600);
    const results = [company(1), company(2)];
    const view = (onCompanyClick: () => void) => (
      <SearchResults
        loading={false}
        results={results}
        onCompanyClick={onCompanyClick}
      />
    );
    const { rerender } = render(view(() => {}));
    const before = screen.getByText("Company 2");

    rerender(view(() => {}));

    expect(screen.getByText("Company 2")).toBe(before);
  });

  it("shows an inactive chip and status date for a known inactive company", () => {
    setViewportWidth(1440);
    const inactiveCompany = {
      ...company(3),
      value: {
        ...company(3).value,
        operating_status: { value: "inactive" },
        status_date: { value: "2024-03-12" },
      },
    };

    render(
      <SearchResults
        loading={false}
        results={[inactiveCompany]}
        onCompanyClick={() => {}}
      />,
    );

    expect(screen.getByText("Inactive")).toBeInTheDocument();
    expect(screen.getByText("Status date: 2024-03-12")).toBeInTheDocument();
  });

  it("shows nothing for active or unknown operating status", () => {
    setViewportWidth(600);
    const activeCompany = {
      ...company(4),
      value: { ...company(4).value, operating_status: { value: "active" } },
    };

    render(
      <SearchResults
        loading={false}
        results={[company(5), activeCompany]}
        onCompanyClick={() => {}}
      />,
    );

    expect(screen.queryByText("Inactive")).not.toBeInTheDocument();
    expect(screen.queryByText(/Status date:/)).not.toBeInTheDocument();
    expect(screen.queryByText(/Acquired by/)).not.toBeInTheDocument();
  });

  it("shows the acquirer name when present on an inactive company", () => {
    setViewportWidth(600);
    const acquiredCompany = {
      ...company(6),
      value: {
        ...company(6).value,
        operating_status: { value: "inactive" },
        acquirer: { value: { name: { value: "Acquirer Bio" } } },
      },
    };

    render(
      <SearchResults
        loading={false}
        results={[acquiredCompany]}
        onCompanyClick={() => {}}
      />,
    );

    expect(screen.getByText("Inactive")).toBeInTheDocument();
    expect(screen.getByText("Acquired by Acquirer Bio")).toBeInTheDocument();
  });
});
